// ---- Utility functions ----

function todayISO() {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function dateOnlyParts(value) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec((value || '').trim());
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const monthDays = [31, (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0 ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  if (month < 1 || month > 12 || day < 1 || day > monthDays[month - 1]) return null;

  return { year, month, day };
}

function dateOnlyDayNumber(value) {
  const parts = dateOnlyParts(value);
  if (!parts) return null;

  const previousYear = parts.year - 1;
  const daysBeforeMonth = [0, 31, 59, 90, 120, 151, 181, 212, 243, 273, 304, 334];
  const leapDay = parts.month > 2 && ((parts.year % 4 === 0 && parts.year % 100 !== 0) || parts.year % 400 === 0) ? 1 : 0;

  return (365 * previousYear)
    + Math.floor(previousYear / 4)
    - Math.floor(previousYear / 100)
    + Math.floor(previousYear / 400)
    + daysBeforeMonth[parts.month - 1]
    + leapDay
    + parts.day;
}

function daysBetween(a, b) {
  const first = dateOnlyDayNumber(a);
  const second = dateOnlyDayNumber(b);
  return first == null || second == null ? null : second - first;
}

function prettyDate(iso) {
  if (!iso) return '—';
  const parts = dateOnlyParts(iso);
  if (!parts) return iso;

  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${monthNames[parts.month - 1]} ${parts.day}`;
}

function uid() {
  return 'j_' + Math.random().toString(36).slice(2, 9);
}

function parseSalaryAmountToken(token) {
  const cleaned = String(token || '').replace(/\$/g, '').replace(/\s/g, '');
  const match = /^(\d+(?:,\d{3})*(?:\.\d+)?)(k)?$/i.exec(cleaned);
  if (!match) return null;

  const amount = Number(match[1].replace(/,/g, ''));
  if (!Number.isFinite(amount) || amount <= 0) return null;

  // Existing UI values are stored as shorthand thousands, e.g. "$150".
  // Full-dollar and explicit "k" CSV values must not be multiplied twice.
  const normalizedAmount = match[2] || (!match[1].includes(',') && amount < 1000)
    ? amount * 1000
    : amount;
  return Math.round(normalizedAmount);
}

function parseSalaryRange(value) {
  const cleaned = String(value || '').trim().replace(/[–—]/g, '-');
  if (!cleaned || cleaned.includes('<') || /\b(hour|hourly|hr)\b|\/\s*(h|hr|hour)\b/i.test(cleaned)) {
    return null;
  }

  const match = /^(\$?\s*\d[\d,]*(?:\.\d+)?\s*k?)\s*(?:-\s*(\$?\s*\d[\d,]*(?:\.\d+)?\s*k?))?\s*(\+)?$/i.exec(cleaned);
  if (!match || (match[2] && match[3])) return null;

  const low = parseSalaryAmountToken(match[1]);
  const high = match[2] ? parseSalaryAmountToken(match[2]) : low;
  if (low == null || high == null || high < low) return null;

  return {
    low,
    high,
    midpoint: (low + high) / 2,
    openEnded: Boolean(match[3]),
  };
}

function parseSalaryValue(value) {
  const range = parseSalaryRange(value);
  return range ? range.midpoint : null;
}

function formatSalaryValue(value) {
  const range = parseSalaryRange(value);
  if (!range) return String(value || '').trim();

  const format = amount => `$${Math.round(amount).toLocaleString()}`;
  if (range.openEnded) return `${format(range.low)}+`;
  if (range.low === range.high) return format(range.low);
  return `${format(range.low)} - ${format(range.high)}`;
}

function formatSalaryCompact(value) {
  const range = parseSalaryRange(value);
  if (!range) return null;
  const k = n => `$${Math.round(n / 1000)}k`;
  if (range.openEnded) return `${k(range.low)}+`;
  if (range.low === range.high) return k(range.low);
  return `${k(range.low)}–${k(range.high)}`;
}

function salaryNumberToBand(value, isOpenEnded = false) {
  if (!Number.isFinite(value)) return null;
  if (isOpenEnded || value > 200) return '$200+';
  if (value > 150) return '$150 - $200';
  if (value > 130) return '$130 - $150';
  if (value >= 100) return '$100 - $130';
  return null;
}

function salaryToBands(s) {
  const range = parseSalaryRange(s);
  if (!range) return [];

  const first = range.low / 1000;
  const last = range.high / 1000;
  const bands = [
    salaryNumberToBand(first),
    salaryNumberToBand(last, range.openEnded),
  ].filter(Boolean);

  return [...new Set(bands)];
}

function salaryToBand(s) {
  return salaryToBands(s)[0] || null;
}

function salarySortNumber(value) {
  const range = parseSalaryRange(value);
  return range ? range.high : -Infinity;
}

function jobCreatedTime(job) {
  const value = job && (job.created_at || job.updated_at);
  const timestamp = value ? Date.parse(value) : NaN;
  return Number.isFinite(timestamp) ? timestamp : null;
}

function compareSameDayJobsNewestFirst(a, b) {
  const firstTime = jobCreatedTime(a);
  const secondTime = jobCreatedTime(b);

  if (firstTime != null && secondTime != null && firstTime !== secondTime) {
    return secondTime - firstTime;
  }
  if (firstTime != null && secondTime == null) return -1;
  if (firstTime == null && secondTime != null) return 1;

  return String(b.id || '').localeCompare(String(a.id || ''));
}

function compareJobsByAppliedDate(a, b, direction = 'desc') {
  const firstDate = String(a.date || '');
  const secondDate = String(b.date || '');
  const dateComparison = direction === 'asc'
    ? firstDate.localeCompare(secondDate)
    : secondDate.localeCompare(firstDate);

  return dateComparison || compareSameDayJobsNewestFirst(a, b);
}

function sortFromOption(option) {
  const [key, dir = 'asc'] = (option || 'date-desc').split('-');
  return { key, dir };
}

function sortOptionFromParts(key, dir) {
  const value = `${key}-${dir}`;
  return SORT_OPTIONS.some(option => option.value === value) ? value : null;
}

function statusSortNumber(status) {
  return STATUS_SORT_ORDER[status] ?? Number.MAX_SAFE_INTEGER;
}

const SUPABASE_URL = 'https://kpyfeihhytxvqkwvxmzs.supabase.co';
const SUPABASE_KEY = 'sb_publishable_W_FUXDNALayxgvCLt3dDPg_bo5usiW_';
const supabaseClient = supabase.createClient(SUPABASE_URL, SUPABASE_KEY);

async function loadJobs() {
  const jobs = [];

  for (let offset = 0; ; offset += JOB_FETCH_PAGE_SIZE) {
    const { data, error } = await supabaseClient
      .from('jobs')
      .select('*')
      .order('id', { ascending: true })
      .range(offset, offset + JOB_FETCH_PAGE_SIZE - 1);

    if (error || !data) {
      if (offset === 0) return SEED_JOBS || [];
      throw error || new Error('Could not load all applications.');
    }

    jobs.push(...data.map(j => {
      const base = j.status === 'waiting' ? { ...j, status: 'applied' } : j;
      return {
        ...base,
        contactName: base.contact_name ?? base.contact ?? '',
        contactEmail: base.contact_email || '',
        contactLinkedIn: base.contact_linkedin || '',
        sentEmail: !!base.sent_email,
        sentEmailDate: base.sent_email_date || '',
      };
    }));
    if (data.length < JOB_FETCH_PAGE_SIZE) return jobs;
  }
}

async function saveJob(job) {
  const { data: { user }, error: userError } = await supabaseClient.auth.getUser();

  if (userError || !user) {
    window.location.href = 'login.html';
    return { data: null, error: userError || new Error('Please log in again.') };
  }

  const contactName = job.contactName ?? job.contact ?? '';
  const clean = {
    id: job.id,
    company: job.company,
    title: job.title,
    date: job.date,
    status: job.status,
    salary: job.salary,
    notes: job.notes,
    contact: contactName,
    contact_name: contactName,
    contact_email: job.contactEmail || '',
    contact_linkedin: job.contactLinkedIn || '',
    sent_email: job.sentEmail || false,
    sent_email_date: job.sentEmailDate || null,
    link: job.link,
    favorite: job.favorite ?? false,
    user_id: user.id,
  };
  const { data: raw, error } = await supabaseClient.from('jobs').upsert(clean).select('*').single();
  const data = raw ? {
    ...raw,
    contactName: raw.contact_name ?? raw.contact ?? '',
    contactEmail: raw.contact_email || '',
    contactLinkedIn: raw.contact_linkedin || '',
    sentEmail: !!raw.sent_email,
    sentEmailDate: raw.sent_email_date || '',
  } : null;
  return { data, error };
}

async function deleteJob(id) {
  await supabaseClient.from('jobs').delete().eq('id', id);
}

async function deleteJobs(ids) {
  if (!ids || ids.length === 0) return;
  await supabaseClient.from('jobs').delete().in('id', ids);
}

async function bulkSetFavorite(ids, value) {
  if (!ids || ids.length === 0) return;
  await supabaseClient.from('jobs').update({ favorite: value }).in('id', ids);
}

async function bulkSetTitle(ids, title) {
  if (!ids || ids.length === 0) return;
  await supabaseClient.from('jobs').update({ title }).in('id', ids);
}

// Returns the URL unchanged only when it starts with http:// or https://.
// Any other protocol (javascript:, data:, vbscript:, etc.) returns '' so
// components can safely use it as an href without risking script execution.
function safeHref(url) {
  if (!url || typeof url !== 'string') return '';
  const trimmed = url.trim();
  return /^https?:\/\//i.test(trimmed) ? trimmed : '';
}
